// src/components/ProductionLog.js
import React, { useEffect, useMemo, useState } from 'react';
import {
  collection,
  doc,
  onSnapshot,
  writeBatch,
  Timestamp,
  serverTimestamp,
} from 'firebase/firestore';
import { db } from '../firebase';

function round2(n) {
  return Math.round((Number(n) + Number.EPSILON) * 100) / 100;
}

function slug(s) {
  return String(s || '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

function getLocalDateString() {
  const now = new Date();
  const y = now.getFullYear();
  const m = String(now.getMonth() + 1).padStart(2, '0');
  const d = String(now.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function shouldIgnoreIngredient(ingredient) {
  const product = String(ingredient?.product || '').trim().toLowerCase();

  return (
    !ingredient?.code ||
    product.includes('pulp') ||
    product.startsWith('water')
  );
}

export default function ProductionLog() {
  const [recipes, setRecipes] = useState([]);
  const [entries, setEntries] = useState({});
  const [shopStock, setShopStock] = useState({});
  const [summary, setSummary] = useState([]);
  const [combinedIngredients, setCombinedIngredients] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [step, setStep] = useState(1);

  useEffect(() => {
    const recipesRef = collection(db, 'recipes');
    const unsubscribe = onSnapshot(recipesRef, (snap) => {
      const list = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
      setRecipes(list);

      setEntries((prev) => {
        const next = { ...prev };
        list.forEach((r) => {
          if (!(r.name in next)) next[r.name] = 0;
        });
        return next;
      });
    });

    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const stockRef = collection(db, 'stock_shop');
    const unsubscribe = onSnapshot(stockRef, (snap) => {
      const map = {};
      snap.docs.forEach((d) => {
        map[d.id] = d.data();
      });
      setShopStock(map);
    });

    return () => unsubscribe();
  }, []);

  const recipeMap = useMemo(() => {
    const map = {};
    recipes.forEach((r) => {
      map[r.name] = r;
    });
    return map;
  }, [recipes]);

  const handleChange = (flavour, value) => {
    if (value === '') {
      setEntries((prev) => ({
        ...prev,
        [flavour]: '',
      }));
      return;
    }

    const parsed = parseFloat(value);

    setEntries((prev) => ({
      ...prev,
      [flavour]: Number.isNaN(parsed) || parsed < 0 ? 0 : parsed,
    }));
  };

  const adjustEntry = (flavour, delta) => {
    setEntries((prev) => {
      const current = Number(prev[flavour] || 0);
      const next = round2(current + delta);

      return {
        ...prev,
        [flavour]: next < 0 ? 0 : next,
      };
    });
  };

  const buildSubmissionData = () => {
    const flavourSummary = [];
    const combinedMap = {};

    for (const [name, rawQty] of Object.entries(entries)) {
      const qty = Number(rawQty || 0);
      if (qty <= 0) continue;

      const recipe = recipeMap[name];
      if (!recipe) continue;

      const stockIngredients = Array.isArray(recipe.ingredients)
        ? recipe.ingredients.filter((i) => !shouldIgnoreIngredient(i))
        : [];

      const ingredientsUsed = stockIngredients.map((i) => {
        const code = String(i.code).trim();
        const requiredKg = round2((Number(i.amount_g || 0) * qty) / 1000);
        const stockData = shopStock[code] || {};
        const availableKg = Number(stockData.totalKg || 0);
        const boxWeight = Number(stockData.boxWeight || 0);

        if (!combinedMap[code]) {
          combinedMap[code] = {
            code,
            product: i.product,
            requiredKg: 0,
            availableKg,
            boxWeight,
          };
        }

        combinedMap[code].requiredKg += requiredKg;
        combinedMap[code].availableKg = Number(shopStock[code]?.totalKg || 0);
        combinedMap[code].boxWeight = Number(shopStock[code]?.boxWeight || 0);

        return {
          code,
          product: i.product,
          requiredKg,
          availableKg,
          boxWeight,
        };
      });

      flavourSummary.push({
        flavour: name,
        quantity: qty,
        ingredientsUsed,
      });
    }

    const combinedIngredients = Object.values(combinedMap).map((item) => ({
      ...item,
      requiredKg: round2(item.requiredKg),
      availableKg: round2(item.availableKg),
      sufficient: item.availableKg >= item.requiredKg,
    }));

    return { flavourSummary, combinedIngredients };
  };

  const handleReview = () => {
    const { flavourSummary, combinedIngredients } = buildSubmissionData();

    if (!flavourSummary.length) {
      alert('Please enter at least one production quantity greater than 0.');
      return;
    }

    const insufficient = combinedIngredients.filter((i) => !i.sufficient);

    if (insufficient.length > 0) {
      alert(
        `❌ Insufficient stock for: ${insufficient
          .map(
            (i) =>
              `${i.product} (${i.requiredKg.toFixed(2)} kg needed, ${i.availableKg.toFixed(2)} kg available)`
          )
          .join(', ')}`
      );
      return;
    }

    setSummary(flavourSummary);
    setCombinedIngredients(combinedIngredients);
    setStep(2);
  };

  const handleConfirm = async () => {
    setSubmitting(true);

    try {
      const dateStr = getLocalDateString();
      const nowMs = Date.now();

      const { flavourSummary, combinedIngredients } = buildSubmissionData();

      if (!flavourSummary.length) {
        alert('Nothing to submit.');
        setSubmitting(false);
        return;
      }

      const insufficient = combinedIngredients.filter(
        (i) => i.availableKg < i.requiredKg
      );

      if (insufficient.length > 0) {
        alert(
          `❌ Stock changed before submit. Please review again.\n\n${insufficient
            .map(
              (i) =>
                `${i.product}: need ${i.requiredKg.toFixed(2)} kg, available ${i.availableKg.toFixed(2)} kg`
            )
            .join('\n')}`
        );
        setSubmitting(false);
        return;
      }

      const batch = writeBatch(db);

      flavourSummary.forEach((entry, index) => {
        const logId = `${dateStr}-${slug(entry.flavour)}-${nowMs}-${index}`;
        batch.set(doc(db, 'production_logs', logId), {
          flavour: entry.flavour,
          quantity: entry.quantity,
          date: dateStr,
          timestamp: Timestamp.now(),
        });
      });

      combinedIngredients.forEach((ing, index) => {
        const stockRef = doc(db, 'stock_shop', ing.code);
        const stockData = shopStock[ing.code] || {};
        const oldKg = Number(stockData.totalKg || 0);
        const newKg = round2(oldKg - ing.requiredKg);

        batch.set(
          stockRef,
          {
            totalKg: newKg,
          },
          { merge: true }
        );

        const auditId = `${slug(ing.product)}-${dateStr}-${nowMs}-${index}`;

        batch.set(doc(db, 'shop_stock_audits', auditId), {
          ingredient: ing.product,
          stockId: ing.code,
          requiredKg: ing.requiredKg,
          oldKg: round2(oldKg),
          newKg,
          status: 'success',
          timestamp: serverTimestamp(),
          source: 'production_log',
          date: dateStr,
        });
      });

      await batch.commit();

      alert('✅ Production logged and stock updated.');

      setEntries((prev) =>
        Object.fromEntries(Object.keys(prev).map((k) => [k, 0]))
      );
      setSummary([]);
      setCombinedIngredients([]);
      setStep(1);
    } catch (err) {
      console.error('Production log failed:', err);
      alert('⚠️ Update failed. Check the console for details.');
    } finally {
      setSubmitting(false);
    }
  };

  if (step === 2) {
    return (
      <div className="production-log">
        <h2>📋 Confirm Production Summary</h2>

        {summary.map((s) => (
          <div key={s.flavour} className="summary-block">
            <h4>
              {s.flavour} — {s.quantity} pan(s)
            </h4>
            <ul>
              {s.ingredientsUsed.map((i) => (
                <li key={`${s.flavour}-${i.code}`}>
                  {i.product}: {i.requiredKg.toFixed(2)} kg
                </li>
              ))}
            </ul>
          </div>
        ))}

        <div className="summary-block" style={{ marginTop: 20 }}>
          <h4>Combined ingredient deduction</h4>
          <ul>
            {combinedIngredients.map((i) => (
              <li key={i.code}>
                {i.product}: {i.requiredKg.toFixed(2)} kg (
                available: {i.availableKg.toFixed(2)} kg)
              </li>
            ))}
          </ul>
        </div>

        <div className="btn-row">
          <button
            onClick={handleConfirm}
            disabled={submitting}
            className="btn btn-primary"
          >
            {submitting ? 'Submitting…' : '✅ Confirm & Submit'}
          </button>

          <button
            onClick={() => setStep(1)}
            className="btn"
            style={{ marginLeft: 10 }}
            disabled={submitting}
          >
            🔙 Go Back
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="production-log">
      <h2>🧾 Production Log</h2>
      <p className="flavour-name">
        Enter how many pans of each flavour were made today
        <strong> (half-pans = 0.5)</strong>:
      </p>

      {recipes.map((r) => (
        <div className="flavour-entry-wrapper" key={r.name}>
          <div className="flavour-text">
            <div className="flavour-name">{r.name}</div>
            <div className="flavour-details">
              {(Array.isArray(r.ingredients) ? r.ingredients : [])
                .filter((i) => !shouldIgnoreIngredient(i))
                .map((i) => {
                  const stock = i.code ? shopStock[i.code] : null;
                  return `${i.product}: ${stock?.totalKg?.toFixed(1) ?? '—'} kg`;
                })
                .join(', ')}
            </div>
          </div>

          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
            }}
          >
            <button
              type="button"
              className="btn"
              onClick={() => adjustEntry(r.name, -0.5)}
              disabled={Number(entries[r.name] || 0) <= 0}
            >
              ➖
            </button>

            <input
              type="number"
              min="0"
              step="0.5"
              value={entries[r.name] ?? 0}
              onChange={(e) => handleChange(r.name, e.target.value)}
              onWheel={(e) => e.currentTarget.blur()}
              className="input"
              style={{
                width: 80,
                textAlign: 'center',
              }}
            />

            <button
              type="button"
              className="btn"
              onClick={() => adjustEntry(r.name, 0.5)}
            >
              ➕
            </button>
          </div>
        </div>
      ))}

      <button
        onClick={handleReview}
        disabled={submitting}
        className="btn btn-primary"
        style={{ marginTop: 20 }}
      >
        Next ➡️
      </button>
    </div>
  );
}